import { useState, useEffect, useRef } from 'react';

import { SpeakerVideoConfig } from '../types';

/**
 * useSpeakerVideo Hook
 *
 * Gère l'état et le comportement de la vidéo speaker circulaire.
 *
 * Fonctionnalités:
 * - Gestion du chargement et de la lecture vidéo
 * - Calcul de la progression en temps réel
 * - Animations d'apparition/disparition
 * - Détection de fin de vidéo
 *
 * Cycle de vie:
 * 1. Quand videoUrl change → reset des états, scale-in, auto-play
 * 2. Pendant la lecture → mise à jour de currentProgress
 * 3. À la fin → scale-out, puis appel de onVideoEnd
 *
 * @param videoUrl - URL de la vidéo à lire (null = pas de vidéo)
 * @param onVideoEnd - Callback appelé après l'animation de disparition
 * @param config - Configuration (durées d'animation, etc.)
 * @returns États et ref pour le composant SpeakerVideo
 *
 * @example
 * ```tsx
 * const { videoRef, isPlaying, currentProgress, isVisible, isLoading, isComplete } =
 *   useSpeakerVideo(videoUrl, handleVideoEnd, config);
 * ```
 */
export const useSpeakerVideo = (
  videoUrl: string | null,
  onVideoEnd: () => void,
  config: SpeakerVideoConfig
) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isComplete, setIsComplete] = useState(false);

  // Toggle play/pause
  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch((error) => {
        console.warn('SpeakerVideo play failed:', error);
      });
    } else {
      video.pause();
    }
  };

  // Auto-play et animation scale-in quand videoUrl change
  useEffect(() => {
    if (videoUrl && videoRef.current) {
      setIsLoading(true);
      setIsComplete(false);
      // Petit délai pour l'animation scale-in
      setTimeout(() => setIsVisible(true), 50);

      videoRef.current.play().catch((error) => {
        console.warn('SpeakerVideo autoplay failed:', error);
      });
      setIsPlaying(true);
    }

    // Cleanup: Stop video when videoUrl changes or becomes null
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };
  }, [videoUrl]);

  // Mise à jour de la progression et gestion des événements vidéo
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Met à jour la barre de progression
    const updateProgress = () => {
      const progress = (video.currentTime / video.duration) * 100;
      setCurrentProgress(progress || 0);
    };

    // Gère la fin de la vidéo
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentProgress(100);
      setIsComplete(true);

      // Animation scale-out avant d'appeler onVideoEnd
      setIsVisible(false);
      setTimeout(() => {
        onVideoEnd?.();
      }, config.animation.scaleOutDuration);
    };

    // Masque le loader quand la vidéo est prête
    const handleLoadedData = () => {
      setIsLoading(false);
    };

    // Synchronize isPlaying state with video events
    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('timeupdate', updateProgress);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [onVideoEnd, config.animation.scaleOutDuration]);

  return {
    videoRef,
    isPlaying,
    currentProgress,
    isVisible,
    isLoading,
    isComplete,
    setIsLoading,
    togglePlayPause,
  };
};
